/**
 * EventHighlightsModeration — Iter-313j admin page.
 *
 * Queue of uploaded event photos awaiting moderation:
 *   Pending → Approve · Reject (with reason) · Hide (for previously approved).
 *
 * Iter-313j.1 — Static class map (Tailwind JIT anti-pattern fix) +
 * per-tab counts (queue depth visible before clicking each tab).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { ArrowLeft, CheckCircle, XCircle, EyeOff, Loader2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

// Static class map — Tailwind JIT cannot process interpolated
// `data-[state=active]:${var}` strings; must be full literals.
const STATUS_TABS = [
  {
    key: 'pending',
    label: 'Pending',
    activeClass: 'data-[state=active]:bg-amber-100 data-[state=active]:text-amber-800',
    badgeClass: 'bg-amber-100 text-amber-700',
  },
  {
    key: 'approved',
    label: 'Approved',
    activeClass: 'data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-800',
    badgeClass: 'bg-emerald-100 text-emerald-700',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    activeClass: 'data-[state=active]:bg-rose-100 data-[state=active]:text-rose-800',
    badgeClass: 'bg-rose-100 text-rose-700',
  },
  {
    key: 'hidden',
    label: 'Hidden',
    activeClass: 'data-[state=active]:bg-slate-200 data-[state=active]:text-slate-800',
    badgeClass: 'bg-slate-200 text-slate-700',
  },
];

const EventHighlightsModeration = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, hidden: 0 });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const loadCounts = useCallback(async () => {
    try {
      const r = await api.get('/admin/event-highlights/counts');
      setCounts(r.data || {});
    } catch { /* silent */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/event-highlights?status=${status}&limit=100`);
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load moderation queue');
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); loadCounts(); }, [load, loadCounts]);

  const act = async (id, action, extra = {}) => {
    setBusyId(id);
    try {
      await api.post(`/admin/event-highlights/${id}/${action}`, extra);
      toast.success(`Photo ${action}d`);
      await load();
      await loadCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action}`);
    } finally { setBusyId(null); }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    await act(rejectTarget.id, 'reject', { reason: rejectReason.trim() || null });
    setRejectTarget(null); setRejectReason('');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="event-highlights-moderation-page">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="mb-3">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to admin
        </Button>
        <h1 className="text-xl font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-cyan-600" />
          Event Highlights Moderation
        </h1>
        <p className="text-sm text-gray-600 mb-5">
          Review talent, client, and admin-uploaded event photos before they appear on the public Community Feed.
        </p>

        <Tabs value={status} onValueChange={setStatus}>
          <TabsList className="mb-4 bg-white flex-wrap h-auto">
            {STATUS_TABS.map((t) => (
              <TabsTrigger
                key={t.key} value={t.key}
                className={`${t.activeClass} gap-1.5`}
                data-testid={`eh-tab-${t.key}`}
              >
                {t.label}
                <Badge
                  variant="secondary"
                  className={`${t.badgeClass} px-1.5 h-4 text-[10px] leading-none`}
                  data-testid={`eh-tab-count-${t.key}`}
                >
                  {counts?.[t.key] ?? 0}
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>

          {STATUS_TABS.map((t) => (
            <TabsContent key={t.key} value={t.key}>
              {loading ? (
                <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
              ) : items.length === 0 ? (
                <Card className="bg-white"><CardContent className="p-8 text-center text-sm text-gray-500">No {t.label.toLowerCase()} highlights.</CardContent></Card>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((h) => (
                    <Card key={h.id} data-testid={`eh-card-${h.id}`} className="overflow-hidden">
                      <div className="aspect-[4/3] bg-slate-100">
                        <img src={h.photo_url} alt="" className="w-full h-full object-cover" />
                      </div>
                      <CardContent className="p-3 space-y-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge variant="outline" className="text-[10px] capitalize">{h.uploader_role}</Badge>
                          <span className="text-[10px] text-gray-500 truncate">{h.uploader_email}</span>
                        </div>
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {h.event_title || 'Untitled event'}
                        </div>
                        <div className="text-[11px] text-gray-600 truncate">
                          {[h.city, h.state].filter(Boolean).join(', ')}
                          {h.shift_date ? ` · ${new Date(h.shift_date).toLocaleDateString()}` : ''}
                          {h.client_company_name ? ` · ${h.client_company_name}` : ''}
                        </div>
                        {h.caption && <p className="text-[12px] text-gray-700 line-clamp-2">{h.caption}</p>}
                        {h.rejection_reason && (
                          <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-100 rounded p-1.5">
                            <strong>Reason:</strong> {h.rejection_reason}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {status !== 'approved' && (
                            <Button
                              size="sm"
                              disabled={busyId === h.id}
                              onClick={() => act(h.id, 'approve')}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-[11px]"
                              data-testid={`eh-approve-${h.id}`}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" /> Approve
                            </Button>
                          )}
                          {status !== 'rejected' && (
                            <Button
                              size="sm" variant="outline"
                              disabled={busyId === h.id}
                              onClick={() => { setRejectTarget(h); setRejectReason(h.rejection_reason || ''); }}
                              className="text-rose-700 border-rose-200 hover:bg-rose-50 h-7 text-[11px]"
                              data-testid={`eh-reject-${h.id}`}
                            >
                              <XCircle className="w-3 h-3 mr-1" /> Reject
                            </Button>
                          )}
                          {status === 'approved' && (
                            <Button
                              size="sm" variant="outline"
                              disabled={busyId === h.id}
                              onClick={() => act(h.id, 'hide')}
                              className="text-slate-700 border-slate-200 hover:bg-slate-100 h-7 text-[11px]"
                              data-testid={`eh-hide-${h.id}`}
                            >
                              <EyeOff className="w-3 h-3 mr-1" /> Hide
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(v) => { if (!v) { setRejectTarget(null); setRejectReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="eh-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject photo</DialogTitle>
            <DialogDescription>Optionally leave a short reason. The uploader will see it in their My Uploads view.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Blurry / off-brand / not from an iEvntür event"
            rows={3}
            data-testid="eh-reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>Cancel</Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={submitReject} data-testid="eh-confirm-reject">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventHighlightsModeration;
